import {
  LOAD_TEST_INPUT_START,
  LOAD_TEST_INPUT_ERROR,
  LOAD_TEST_INPUT_SUCCESS,
  RcaThunkDispatch,
  EXECUTE_TEST_START,
  EXECUTE_TEST_SUCCESS,
  SET_SERVER_VALIDATION_ERRORS,
  UPDATE_TEST_INPUT_FORM_DATA,
  EXECUTE_TEST_THROTTLED,
  EXECUTE_TEST_USER_UNKNOWN,
  CLEAR_SERVER_VALIDATION_ERRORS,
  CLEAR_SPECIFIC_SERVER_VALIDATION_ERROR,
  SET_USERNAME_HINT,
  SET_FOCUSED_FIELD_ID,
} from "./actionTypes";
import { actions as proofOfWorkActions } from "../features/proofOfWork/proofOfWorkSlice";
import { AnyAction } from "@reduxjs/toolkit";
import { ITestInputForm } from "../models/ITestInputForm";
import { IRcaState } from "../store/IRcaState";
import { IServices } from "../services/IServices";
import * as H from "history";
import { IServerValidationError } from "../models/IServerValidationError";
import { IOAuthAccount } from "../models/IOAuthAccount";
import { IOAuthCredential } from "../models/IOAuthCredential";

/**
 * Creates an action indicating the test input has started loading
 *
 * @returns The action
 */
const loadTestInputStart = (): AnyAction => ({
  type: LOAD_TEST_INPUT_START,
});

/**
 * Creates an action indicating the loading of the test input failed
 *
 * @returns The action
 */
const loadTestInputError = (): AnyAction => ({
  type: LOAD_TEST_INPUT_ERROR,
});

/**
 * Creates an action indicating the loading of the test input succeeded
 *
 * @param testInput - The test input form
 * @returns The action
 */
const loadTestInputSuccess = (testInput: ITestInputForm): AnyAction => ({
  type: LOAD_TEST_INPUT_SUCCESS,
  testInput,
});

/**
 * Loads the test input form for the test specified
 *
 * @param language - The language
 * @param testId - The test id
 * @returns The thunk action
 */
export const loadTestInput = (language: string, testId: string) => {
  return async (dispatch: RcaThunkDispatch, _getState: () => IRcaState, services: IServices): Promise<void> => {
    dispatch(loadTestInputStart());
    try {
      const results = await services.testCatalog.getInputFormAsync(language, testId);
      dispatch(loadTestInputSuccess(results));
    } catch (error) {
      dispatch(loadTestInputError());
    }
  };
};

/**
 * Updates the input form data
 *
 * @param formData - the updated form data
 * @returns the action
 */
export const updateInputFormData = (formData: Record<string, unknown>): AnyAction => ({
  type: UPDATE_TEST_INPUT_FORM_DATA,
  formData,
});

/**
 * Creates an action indicating we are sending the request to execute the test
 *
 * @returns The action
 */
const executeTestStart = (): AnyAction => ({
  type: EXECUTE_TEST_START,
});

/**
 * Creates an action indicating we started the test execution successfully.
 *
 * @returns The action
 */
const executeTestSuccess = (): AnyAction => ({
  type: EXECUTE_TEST_SUCCESS,
});

/**
 * Creates an action indicating we got server-side validation errors when executing the test.
 *
 * @param validationErrors - the validation errors
 * @returns The action
 */
const setServerValidationErrors = (validationErrors: IServerValidationError[]): AnyAction => ({
  type: SET_SERVER_VALIDATION_ERRORS,
  validationErrors,
});

/**
 * Creates an action that clears any server-side validation errors
 *
 * @returns The action
 */
export const clearServerSideValidationErrors = (): AnyAction => ({
  type: CLEAR_SERVER_VALIDATION_ERRORS,
});

/**
 * Creates an action that clears validation errors from a specific field
 *
 * @param field - The field to clear
 * @returns The action
 */
export const clearSpecificServerValidationError = (field: string): AnyAction => ({
  type: CLEAR_SPECIFIC_SERVER_VALIDATION_ERROR,
  field,
});

/**
 * Creates an action sets the error to the specified throttled error.
 *
 * @param message - the error message
 * @returns the action
 */
const executeTestThrottled = (message: string): AnyAction => ({
  type: EXECUTE_TEST_THROTTLED,
  error: message,
});

/**
 * Creates an action sets the error to the specified user unknown error.
 *
 * @param message - the error message
 * @returns the action
 */
const executeTestUserUnknown = (message: string): AnyAction => ({
  type: EXECUTE_TEST_USER_UNKNOWN,
  error: message,
});

/**
 * The name of parameters that contains OAuth credentials
 */
const oAuthCredentialKey = "oAuthCredential";

/**
 * Executes a test with the given parameters
 *
 * @param language - The language
 * @param testId - The test id
 * @param parameters - The test parameters
 * @param history - the history object
 * @returns The thunk action
 */
export const executeTest = (language: string, testId: string, parameters: Record<string, unknown>, history: H.History) => {
  return async (dispatch: RcaThunkDispatch, getState: () => IRcaState, services: IServices): Promise<void> => {
    dispatch(updateInputFormData(parameters));

    // Rebuild the actual parameters we'll send for the test
    // This keeps the token from ever being stored in the Redux state
    const testParameters = { ...parameters };

    // Only process the parameter if it exists in this set of parameters
    const account = testParameters[oAuthCredentialKey] as IOAuthAccount;
    let credential: IOAuthCredential | null = null;
    if (account) {
      // For each account, acquire a token from the cache.
      if (account.homeAccountId) {
        credential = await services.oAuthService.acquireTokensAsync(account.homeAccountId, true);
      } else {
        // Delete the key from the JSON object if it's empty
        delete testParameters[oAuthCredentialKey];
      }
    }

    dispatch(executeTestStart());

    try {
      const {
        globalConfig: { antiForgeryToken },
      } = getState();

      const executeResult = credential
        ? await services.testCatalog.executeTestWithAuthAsync(language, testId, testParameters, antiForgeryToken, credential)
        : await services.testCatalog.executeTestAsync(language, testId, testParameters, antiForgeryToken);

      switch (executeResult.status) {
        case "Success":
          dispatch(executeTestSuccess());
          history.push(`/result/${executeResult.executionId}`);
          break;

        case "Throttled":
          dispatch(executeTestThrottled(executeResult.error));
          break;

        case "UserUnknown":
          dispatch(executeTestUserUnknown(executeResult.error));
          break;
      }

      // Always update server validation errors, whether good or bad
      const errors: IServerValidationError[] = [];
      if (executeResult.validationErrors) {
        for (const fieldName of Object.keys(executeResult.validationErrors)) {
          errors.push({
            fieldName,
            errors: [executeResult.validationErrors[fieldName]],
          });
        }
      }

      // If the server rejected the proof-of-work (e.g., signing-key rotation),
      // reset the widget so the user can re-solve the challenge, and surface the
      // error on the proofOfWork field so it is visible in the UI.
      if (executeResult.hipValidationError) {
        dispatch(proofOfWorkActions.resetVerification());
        errors.push({
          fieldName: "proofOfWork",
          errors: [String(ClientStrings.ProofOfWork_SecurityCheckFailed)],
        });
      }

      // Update any server validation errors
      dispatch(setServerValidationErrors(errors));
    } catch (error) {
      dispatch(loadTestInputError());
    }
  };
};

/**
 * Creates an action that sets the username hint
 *
 * @param hint - the username hint
 * @returns the action
 */
export const setUserNameHint = (hint: string): AnyAction => ({
  type: SET_USERNAME_HINT,
  hint: hint.toUpperCase(),
});

/**
 * Creates an action that sets the id of the field currently in focus
 *
 * @param id - the focused field ID
 * @returns the action
 */
export const setFocusedFieldId = (id: string): AnyAction => ({
  type: SET_FOCUSED_FIELD_ID,
  focusedFieldId: id,
});
